import classNames from 'classnames/bind';
import { InternationalContext } from 'contexts/international';
import { useReloadTimerContext } from 'contexts/reloadTimer';
import { TripType } from 'contexts/searchParams';
import { useContext } from 'react';
import { Popovers, SelectTypeKeys, SortKeys, SortOrders } from '../types';
import styles from './index.module.scss';
import Title from './title';

const cx = classNames.bind(styles);

const sortMap = {
  [SortKeys.price]: {
    typeText: '가격',
    orderText: {
      [SortOrders.ascending]: '낮은순',
      [SortOrders.descending]: '높은순',
    },
    titleText: '가격 순',
    nClickCode: 'isr.priceorder',
  },
  [SortKeys.date]: {
    typeText: '출발시각',
    orderText: {
      [SortOrders.ascending]: '빠른순',
      [SortOrders.descending]: '늦은순',
    },
    titleText: '출발시각 순',
    nClickCode: 'isr.deptime',
  },
  [SortKeys.duration]: {
    typeText: '소요시간',
    orderText: {
      [SortOrders.ascending]: '짧은순',
      [SortOrders.descending]: '긴순',
    },
    titleText: '소요시간 순',
    nClickCode: 'isr.duration',
  },
  [SortKeys.depDate]: {
    typeText: '출발시각 가는날',
    orderText: {
      [SortOrders.ascending]: '빠른순',
      [SortOrders.descending]: '늦은순',
    },
    titleText: '출발시각 순',
    nClickCode: 'isr.deptime',
  },
  [SortKeys.arrDate]: {
    typeText: '출발시각 오는날',
    orderText: {
      [SortOrders.ascending]: '빠른순',
      [SortOrders.descending]: '늦은순',
    },
    titleText: '출발시각 순',
    nClickCode: 'isr.deptimereturn',
  },
  [SortKeys.name]: {
    typeText: '항공사',
    orderText: {
      [SortOrders.ascending]: '가나다',
      [SortOrders.descending]: '다나가',
    },
    titleText: '항공사 순',
    nClickCode: 'isr.airlineorder',
  },
  [SortKeys.depDuration]: {
    typeText: '소요시간 가는날',
    orderText: {
      [SortOrders.ascending]: '짧은순',
      [SortOrders.descending]: '긴순',
    },
    titleText: '소요시간 순',
    nClickCode: 'isr.duration',
  },
  [SortKeys.arrDuration]: {
    typeText: '소요시간 오는날',
    orderText: {
      [SortOrders.ascending]: '짧은순',
      [SortOrders.descending]: '긴순',
    },
    titleText: '소요시간 순',
    nClickCode: 'isr.durationreturn',
  },
};

const commonSortKeys = [
  SortKeys.price,
  SortKeys.date,
  SortKeys.duration,
  SortKeys.name,
];

const concurrentRoundTripSortKeys = [
  SortKeys.price,
  SortKeys.depDate,
  SortKeys.arrDate,
  SortKeys.depDuration,
  SortKeys.arrDuration,
  SortKeys.name,
];

interface HeaderProps {
  excludeCard: boolean;
  popover: Popovers;
  samePriceOrgSch?: string;
  selectType: SelectTypeKeys;
  stayLength?: string;
  targetIndex: number;
  tripType: TripType;
  goBack: () => void;
  onExcludeCardFilterClick(excludeCardFilter: boolean): void;
  onSortByClick: (key: SortKeys, targetIndex: number) => void;
  setSelectTypeAndClose: (s: SelectTypeKeys) => void;
  togglePopover: (popoverKey: Popovers) => void;
}

const InternationalHeader = ({
  excludeCard,
  popover,
  samePriceOrgSch,
  selectType,
  stayLength,
  targetIndex,
  tripType,
  goBack,
  onExcludeCardFilterClick,
  onSortByClick,
  setSelectTypeAndClose,
  togglePopover,
}: HeaderProps) => {
  const {
    sortBy: { key: sortByKey, order: sortOrder },
  } = useContext(InternationalContext);
  const { withReloadTimer } = useReloadTimerContext();
  const sortKeys =
    tripType === TripType.RT && selectType === SelectTypeKeys.concurrent
      ? concurrentRoundTripSortKeys
      : commonSortKeys;
  return (
    <div
      className={cx('InternationalHeader', {
        is_concurrent: selectType === 'concurrent',
      })}
    >
      <div className={cx('inner')}>
        <h3 className={cx('title')}>
          <Title
            tripType={tripType}
            samePriceOrgSch={samePriceOrgSch}
            selectType={selectType}
            popover={popover}
            stayLength={stayLength}
            togglePopover={togglePopover}
            setSelectTypeAndClose={setSelectTypeAndClose}
          />
        </h3>
        {samePriceOrgSch ? (
          <button
            type="button"
            aria-label="닫기"
            className={cx('close')}
            data-event-nlog="click"
            data-event-area-code="isb.close"
            onClick={() => {
              goBack();
            }}
          />
        ) : (
          <>
            <div className={cx('sort')}>
              <button
                type="button"
                className={cx('current')}
                aria-expanded={popover === Popovers.sortBy}
                data-event-nlog="click"
                data-event-area-code="isr.sort"
                onClick={() => {
                  togglePopover(Popovers.sortBy);
                }}
              >
                {sortMap[sortByKey].titleText}
              </button>
              {popover === Popovers.sortBy && (
                <div className={cx('options')}>
                  {sortKeys.map((k, i) => {
                    const { typeText, orderText, nClickCode } = sortMap[k];
                    const selected = sortByKey === k;
                    return (
                      <button
                        key={`sortOption1_${i}`}
                        type="button"
                        className={cx('option')}
                        aria-selected={selected}
                        data-event-nlog="click"
                        data-event-area-code={nClickCode}
                        onClick={withReloadTimer(() => {
                          onSortByClick(k, targetIndex);
                        })}
                      >
                        <span className={cx('txt')}>{typeText}</span>
                        <i
                          className={cx({
                            descending: selected && sortOrder === -1,
                            ascending: selected && sortOrder !== -1,
                          })}
                        >
                          {selected && orderText[sortOrder]}
                        </i>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <div className={cx('sort')}>
              <button
                type="button"
                className={cx('current')}
                aria-expanded={popover === Popovers.payMethodFilter}
                data-event-nlog="click"
                data-event-area-code="isr.faretype"
                onClick={() => {
                  togglePopover(Popovers.payMethodFilter);
                }}
              >
                카드 혜택 {excludeCard ? '제외' : '포함'}
              </button>
              {popover === Popovers.payMethodFilter && (
                <div className={cx('options')}>
                  {[false, true].map(option => (
                    <button
                      key={`${option}`}
                      type="button"
                      className={cx('option')}
                      aria-selected={option === excludeCard}
                      data-event-nlog="click"
                      data-event-area-code={`isr.${
                        option ? 'allfaretype' : 'adultfareonly'
                      }`}
                      onClick={withReloadTimer(() => {
                        onExcludeCardFilterClick(option);
                      })}
                    >
                      <span className={cx('txt')}>
                        카드 혜택 {option ? '제외' : '포함'}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default InternationalHeader;
